import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faFileExcel,
  faFilePdf,
  faClock,
  faUser,
  faIdCard,
  faBriefcase,
  faCheckCircle,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import SearchBar from "../../components/ui/SearchBar";
import TableWrapper from "../../components/ui/TableWrapper";
import EmptyState from "../../components/ui/EmptyState";
import Pagination from "../../components/ui/Pagination";
import DateFilter from "../../components/ui/DateFilter";
import { useUserLog, useUserActivitiesByUser } from "../../hooks";

const UserActivityDetails = () => {
  const { userlogId } = useParams();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isFilterApplied, setIsFilterApplied] = useState(false);

  // Fetch the user log (which contains the user identity)
  const { userLog, loading: userLoading, error: userError } = useUserLog(userlogId);

  // Fetch activities belonging to this userlogId
  const {
    userActivities,
    pagination,
    loading: activitiesLoading,
    error: activitiesError,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    refresh,
  } = useUserActivitiesByUser(userlogId, {
    search: searchTerm || undefined,
    start_date: startDate || undefined,
    end_date: endDate || undefined,
  });

  // Keep filter flag in sync with date inputs
  useEffect(() => {
    setIsFilterApplied(Boolean(startDate && endDate));
  }, [startDate, endDate]);

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleExportExcel = () => {
    console.log("Exporting to Excel...");
  };

  const handleExportPDF = () => {
    console.log("Exporting to PDF...");
  };

  const handleBack = () => {
    navigate("/app/userLogs");
  };

  // ── Loading / error states ────────────────────────────────
  if (userLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <FontAwesomeIcon
          icon={faSpinner}
          spin
          className="h-5 w-5 text-blue-800"
        />
        <p className="text-sm text-gray-500 mt-2">Loading user...</p>
      </div>
    );
  }

  if (userError || !userLog) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <p className="text-gray-600">User not found</p>
        <Button onClick={handleBack} className="mt-4">
          Go Back
        </Button>
      </div>
    );
  }

  // ── Derived values ────────────────────────────────────────
  const user = {
    full_name: userLog.full_name || userLog.userlogId || "Unknown User",
    username: userLog.username,
    role: userLog.ip_address || "—",
    status: userLog.status || "ACTIVE",
  };

  const totalItems = pagination?.total ?? userActivities.length;
  const totalPages = pagination?.last_page ?? 1;
  const startIndex = (currentPage - 1) * pageSize;

  const statusStyles = {
    ACTIVE: "bg-green-50 text-green-700 border-green-200",
    active: "bg-green-50 text-green-700 border-green-200",
    INACTIVE: "bg-red-50 text-red-700 border-red-200",
    inactive: "bg-red-50 text-red-700 border-red-200",
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Back Button & Header */}
      <Card className="bg-white border border-gray-200 px-4 py-3 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-800 text-white hover:bg-blue-900 transition-all"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="h-4 w-4" />
            Back to Logs
          </button>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">User:</span>
            <span className="text-sm font-semibold text-gray-900">
              {user.full_name}
            </span>
            <Badge
              variant="outline"
              className={statusStyles[user.status] || statusStyles.ACTIVE}
            >
              {user.status}
            </Badge>
          </div>
        </div>
      </Card>

      {/* User Info Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-gray-600 uppercase tracking-wider">
              Full Name
            </p>
            <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-blue-100 text-blue-500">
              <FontAwesomeIcon icon={faUser} className="h-3 w-3" />
            </span>
          </div>
          <p className="text-sm font-semibold text-gray-900">
            {user.full_name}
          </p>
        </Card>

        <Card className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-gray-600 uppercase tracking-wider">
              username
            </p>
            <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-purple-100 text-purple-500">
              <FontAwesomeIcon icon={faIdCard} className="h-3 w-3" />
            </span>
          </div>
          <p className="text-sm font-semibold text-gray-900">
            {user.username}
          </p>
        </Card>

        <Card className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-gray-600 uppercase tracking-wider">
              Role
            </p>
            <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-amber-100 text-amber-500">
              <FontAwesomeIcon icon={faBriefcase} className="h-3 w-3" />
            </span>
          </div>
          <p className="text-sm font-semibold text-gray-900 capitalize">
            {user.role}
          </p>
        </Card>

        <Card className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-gray-600 uppercase tracking-wider">
              Status
            </p>
            <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-green-100 text-green-500">
              <FontAwesomeIcon icon={faCheckCircle} className="h-3 w-3" />
            </span>
          </div>
          <p className="text-sm font-semibold text-gray-900 capitalize">
            {user.status}
          </p>
        </Card>
      </div>

      {/* Activities Table */}
      <Card className="bg-white border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <FontAwesomeIcon
                  icon={faClock}
                  className="text-blue-800 h-4 w-4"
                />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  User Activities
                </h3>
                <p className="text-sm text-gray-600">
                  {totalItems} activities recorded for {user.full_name}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="gap-2 border-gray-300 hover:border-blue-800"
                onClick={handleExportExcel}
              >
                <FontAwesomeIcon
                  icon={faFileExcel}
                  className="text-green-600 h-4 w-4"
                />
                Excel
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="gap-2 border-gray-300 hover:border-blue-800"
                onClick={handleExportPDF}
              >
                <FontAwesomeIcon
                  icon={faFilePdf}
                  className="text-red-600 h-4 w-4"
                />
                PDF
              </Button>
            </div>
          </div>
        </div>

        {/* Date Filter */}
        <div className="px-4 sm:px-6 pt-3">
          <DateFilter
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onApply={() => {
              setIsFilterApplied(true);
              setCurrentPage(1);
              refresh();
            }}
            onClear={() => {
              setStartDate("");
              setEndDate("");
              setIsFilterApplied(false);
              setCurrentPage(1);
              refresh();
            }}
            isFilterApplied={isFilterApplied}
            totalItems={totalItems}
            label="activities"
          />
        </div>

        {/* Search */}
        <div className="px-4 sm:px-6 py-3 border-b border-gray-100 bg-gray-50/50">
          <SearchBar
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Search activities..."
            className="w-full"
          />
        </div>

        {/* Table */}
        <TableWrapper
          title="activities"
          totalItems={totalItems}
          currentPage={currentPage}
          totalPages={totalPages}
          className="bg-white border border-gray-200 shadow-sm"
        >
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50 hover:bg-gray-50 border-b-2 border-gray-200">
                <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                  S/N
                </TableHead>
                <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                  <FontAwesomeIcon icon={faUser} className="mr-1.5 h-3 w-3" />
                  Activity
                </TableHead>
                <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                  Module
                </TableHead>
                <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                  Description
                </TableHead>
                <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                  <FontAwesomeIcon icon={faClock} className="mr-1.5 h-3 w-3" />
                  Timestamp
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {activitiesLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <FontAwesomeIcon
                      icon={faSpinner}
                      spin
                      className="h-5 w-5 text-blue-800"
                    />
                    <p className="text-sm text-gray-500 mt-2">
                      Loading activities...
                    </p>
                  </TableCell>
                </TableRow>
              ) : activitiesError ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <EmptyState
                      title="Failed to load activities"
                      message={
                        activitiesError?.message || "Please try again later"
                      }
                    />
                  </TableCell>
                </TableRow>
              ) : userActivities.length > 0 ? (
                userActivities.map((activity, idx) => (
                  <TableRow
                    key={activity.id}
                    className="hover:bg-blue-50/30 transition-colors duration-150 border-b border-gray-100"
                  >
                    <TableCell className="py-2.5 px-3 text-sm text-gray-600">
                      {startIndex + idx + 1}
                    </TableCell>
                    <TableCell className="py-2.5 px-3 text-sm font-medium text-gray-900">
                      <Badge
                        variant="outline"
                        className="bg-blue-50 text-blue-700 border-blue-200"
                      >
                        {activity.activity}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-2.5 px-3 text-sm text-gray-600">
                      <Badge
                        variant="outline"
                        className="bg-purple-50 text-purple-700 border-purple-200"
                      >
                        {activity.module}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-2.5 px-3 text-sm text-gray-700">
                      {activity.description}
                    </TableCell>
                    <TableCell className="py-2.5 px-3 text-sm text-gray-600 whitespace-nowrap">
                      {activity.created_at}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <EmptyState
                      title="No activities found"
                      message={
                        searchTerm || isFilterApplied
                          ? "Try adjusting your search or filters"
                          : "No activities recorded for this user"
                      }
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableWrapper>

        {/* Pagination */}
        {totalItems > 0 && (
          <div className="px-4 sm:px-6 py-3 border-t border-gray-100">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={totalItems}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default UserActivityDetails;